<script setup lang="ts">
import { ref } from 'vue';
import type { CommentReview } from '../order-management';
const props = defineProps<{
  orderId: string;
  comment?: string;
  review?: CommentReview | null;
  readOnly?: boolean;
  disabled?: boolean;
}>();
const emit = defineEmits<{ changed: [] }>();
const reevaluating = ref(false);
const reason = ref(''),
  busy = ref(false),
  error = ref('');
async function submit(feasible: boolean): Promise<void> {
  if (busy.value || props.disabled || props.readOnly || !props.review?.commentVersion) return;
  error.value = '';
  if (!reason.value.trim()) {
    error.value =
      'Précisez comment la demande sera traitée, ou pourquoi elle ne peut pas être réalisée.';
    return;
  }
  busy.value = true;
  try {
    const response = await globalThis.fetch(
      `/provider/api/delivery-requests/${encodeURIComponent(props.orderId)}/comment-review`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          commentVersion: props.review.commentVersion,
          feasible,
          reason: reason.value.trim(),
        }),
      },
    );
    if (!response.ok)
      throw new Error('Contrôle non enregistré. Actualisez la commande et vérifiez son état.');
    emit('changed');
  } catch (cause) {
    error.value = (cause as Error).message;
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <section
    v-if="comment"
    class="comment-review"
    :class="review?.status?.toLowerCase()"
    aria-label="Faisabilité de la demande client"
  >
    <strong>{{
      review?.status === 'APPROVED'
        ? 'Demande client réalisable · contrôle confirmé'
        : review?.status === 'DECLINED'
          ? 'Demande client non réalisable · validation bloquée'
          : 'Commentaire client à vérifier · validation bloquée'
    }}</strong>
    <p v-if="review?.reason">{{ review.reason }}</p>
    <p v-if="review?.providerId">Contrôlé par {{ review.providerId }}</p>
    <template v-if="!readOnly && (review?.status !== 'APPROVED' || reevaluating)">
      <p>
        Confirmez que la composition et les modalités permettent de respecter la demande. Si le
        prix change, faites d’abord traiter la modification de commande.
      </p>
      <label
        >Réponse au commentaire<textarea
          v-model="reason"
          maxlength="128"
          rows="2"
          :disabled="busy || disabled"
        />
      </label>
      <button
        class="feasible"
        type="button"
        :disabled="busy || disabled || !review?.commentVersion"
        @click="submit(true)"
      >
        Confirmer la faisabilité
      </button>
      <button
        class="blocked"
        type="button"
        :disabled="busy || disabled || !review?.commentVersion"
        @click="submit(false)"
      >
        Signaler une impossibilité
      </button>
    </template>
    <button
      v-if="!readOnly && review?.status === 'APPROVED' && !reevaluating"
      class="blocked"
      type="button"
      :disabled="disabled || busy"
      @click="reevaluating = true"
    >
      Réexaminer la faisabilité
    </button>
    <p v-if="error" role="alert">{{ error }}</p>
  </section>
</template>
<style scoped>
.comment-review {
  border: 2px solid #d97706;
  border-radius: 1rem;
  padding: 1rem;
  margin: 0.75rem 0;
  background: #fffbeb;
  overflow-wrap: anywhere;
}
.comment-review.approved {
  border-color: #15803d;
  background: #f0fdf4;
}
.comment-review.declined {
  border-color: #dc2626;
}
label,
textarea {
  display: block;
  width: 100%;
  box-sizing: border-box;
}
textarea {
  margin: 0.5rem 0;
}
button {
  width: 100%;
  min-height: 44px;
  border: 0;
  border-radius: 0.75rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
  font-weight: 700;
}
.feasible {
  background: #15803d;
  color: white;
}
.blocked {
  background: #fde047;
  color: #172033;
}
button:disabled {
  opacity: 0.5;
}
</style>
